import { useCallback, useEffect, useRef, useState } from "react"
import type { SearchableSelectOption } from "@/components/ats-fbo/fields/searchable-select"

export interface UseNewWorkerOrgEditorProps {
  name: string
  options: SearchableSelectOption[]
  /** True while an org search triggered by the latest rename is still in flight. The
   *  exactMatch derivation reads the current options snapshot, so confirming while a search
   *  is settling could let the user past a name that's about to resolve to an existing org. */
  isSearching: boolean
}

export interface UseNewWorkerOrgEditorReturn {
  /** Ref attached to the input so the editor can autofocus on mount and on edit re-entry. */
  inputRef: React.RefObject<HTMLInputElement | null>
  /** Two-mode display: editing (input + ✓/✗) vs confirmed (read-only chip + pencil). */
  confirmed: boolean
  setConfirmed: (value: boolean) => void
  /** Trimmed version of `name` — empty when the user blanks the field. */
  trimmed: string
  /** Existing option whose display or canonical name matches the trimmed input
   *  (case-insensitive). Drives the "an organization with this name already exists" path. */
  exactMatch: SearchableSelectOption | undefined
  /** True when the editor is in a confirmable state: non-empty trimmed name, no exact match,
   *  and no in-flight search. Gates the Enter handler and the ✓ button. */
  canConfirm: boolean
}

/**
 * Encapsulates the state machine of the embedded "Confirm new worker organization" editor:
 *  - confirmed/editing toggle and the autofocus + select-all on entering editing mode
 *  - trimmed-name + exactMatch + canConfirm derivations (with the in-flight-search guard)
 *
 * The owning component still renders the inputs and wires the rename/search callbacks —
 * the hook just owns the state so the component can stay render-only.
 */
export function useNewWorkerOrgEditor({
  name,
  options,
  isSearching,
}: UseNewWorkerOrgEditorProps): UseNewWorkerOrgEditorReturn {
  const inputRef = useRef<HTMLInputElement>(null)
  const [confirmed, setConfirmed] = useState(false)

  // Autofocus + select-all when entering editing mode so the user can immediately refine
  // the proposed name. Re-fires when the user clicks pencil to edit again.
  useEffect(() => {
    if (!confirmed) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [confirmed])

  const trimmed = name.trim()
  const exactMatch = trimmed
    ? options.find((o) => {
        const display = o.name.trim().toLowerCase()
        const canonical = o.canonicalName?.trim().toLowerCase()
        return display === trimmed.toLowerCase() || canonical === trimmed.toLowerCase()
      })
    : undefined
  const canConfirm = trimmed.length > 0 && !exactMatch && !isSearching

  const wrappedSetConfirmed = useCallback((value: boolean) => {
    setConfirmed(value)
  }, [])

  return {
    inputRef,
    confirmed,
    setConfirmed: wrappedSetConfirmed,
    trimmed,
    exactMatch,
    canConfirm,
  }
}
